import {inject, Service} from '@angular/core'
import {BehaviorSubject, distinctUntilChanged, Observable} from 'rxjs'
import {debounceTime, filter, first, map, switchMap} from 'rxjs/operators'
import {KalpBuilding} from '../model/kalp-building'
import {KalpLoan} from '../model/kalp-loan'
import {Mortgage} from '../model/mortgage'
import {Terms, TermsTexts} from '../model/terms-texts'
import {DebtQuota, KalpService, LegacyKalp, ObjectTypes, Scenarios} from './kalp.service'
import {StaticDataService} from './static-data.service'

export interface Proposal {
  /**
   * This is a list of loans as we have "calculated" them
   */
  loans: KalpLoan[]

  /**
   * This is the target amount that we work against, eg. someone wants a loan of SEK 3 000 000
   * that is what we want to split into several loans.
   */
  loanAmount: number

  /**
   * Set this to true if you have made your own proposal
   */
  manual: boolean
}

export interface Questions {
  /**
   * Q3: What if a major event impacts your income or expenses?, 1 - little, 2, some, 3 a lot
   */
  q3: number

  /**
   * Q4: Do you think your household's finances will be significantly affected in the next years?
   * 1 - no, 2 yes positive, 3 yes negative
   */
  q4: number

  /**
   * Q5: Does the household have savings or other investments to cope with increased interest costs?
   * 1 - yes, 2 some, 3 no
   */
  q5: number

  /**
   * Q7: How long do you plan to stay in the property?
   * 1 - less than a year, 2 more than a year
   */
  q6: number
}

@Service()
export class ProposalService {
  /**
   * This is where we publish our proposal
   *
   */
  public proposal$ = new BehaviorSubject<Proposal>({
    loanAmount: 0,
    loans: [],
    manual: false
  })

  /**
   * The current loan amount is set by someone...
   * Actually it is (was) set by the living component...
   */
  private loanAmount = 0

  private questions: Questions = {
    q3: -1,
    q4: -1,
    q5: -1,
    q6: -1
  }

  private scenario: Scenarios = Scenarios.Purchase

  private twoPercent = 0

  private isBostadsratt = false

  /**
   * Once the advisor has made changes, all is fixed until
   * explicitly not so.
   */
  private fixed = false

  private readonly kalpService = inject(KalpService)
  private readonly staticDataService = inject(StaticDataService)


  constructor() {

    this.proposal$.pipe(
      filter((proposal: Proposal) => !!proposal)
    ).subscribe((proposal: Proposal) => this.fixed = proposal.manual)

    this.kalpService.kalpResult$.pipe(
      filter((kalps: LegacyKalp[]) => kalps && kalps.length === 3)
    ).subscribe((kalps: LegacyKalp[]) => {
      this.twoPercent = kalps[2].kalp
    })

    this.kalpService.buildings$.pipe(
      switchMap(() => this.kalpService.getActiveBuilding())
    ).subscribe((building: KalpBuilding) => {
      this.isBostadsratt = building.typeOfObject === ObjectTypes.BOSTADSRATT
    })

    /**
     * Reset the questions (with reset in staticDataService) for the next customer
     */
    this.staticDataService.questions$.subscribe((questions: Questions) => this.questions = questions)

    /**
     * Normally we make a new loan if scenario, questions or loan
     * amount changes/emits
     */
    this.staticDataService.questions$.pipe(
      // Filter based on conditions:
      // - If we have new questions, q3 to q6 are needed
      // - Otherwise, q1 and q2 are needed
      filter((questions: Questions) => {

        // When we have new questions, all of q3 to q6 should not be -1
        return questions.q3 !== -1 && questions.q4 !== -1 && questions.q5 !== -1 && questions.q6 !== -1
      }),
      switchMap((questions: Questions) => {
        this.questions = questions
        return this.kalpService.loanAmount$
      }),
      switchMap((loanAmount: number) => {
        this.loanAmount = loanAmount
        return this.kalpService.scenarioChange$
      }),
      debounceTime(1000),
      // Heads up, this does pass if manual!
      filter((scenario: Scenarios | null): scenario is Scenarios => !!scenario && !this.fixed)
    ).subscribe(((scenario: Scenarios) => {
      this.proposal$.next({loanAmount: 0, loans: [], manual: false})
      this.scenario = scenario
      /**
       * Stupid, but i need to update twice, else I can't get it right when I change answers in questions :(
       */
      this.update()
      this.update()
    }))

    /**
     * Listens for changes when loanAmount > 0 and 'fixed' is false (not manual change).
     * This will trigger KALP update with the lowest term, without requiring answers to all questions.
     */
    this.kalpService.loanAmount$
      .pipe(distinctUntilChanged())
      .subscribe(loanAmount => {
        if (loanAmount > 0 && !this.fixed) {
          this.loanAmount = loanAmount
          this.update()
        }
      })

    // update amortization when recommendedAmortization changes
    this.kalpService.buildings$
      .pipe(
        map(buildings => buildings.find(b => b.primary)?.recommendedAmortization ?? 0),
        distinctUntilChanged(),
        filter(() => this.loanAmount > 0)
      )
      .subscribe(() => {
        this.update()
      })
  }

  public unlockProposal(): void {
    this.proposal$.pipe(
      first(),
      map((proposal: Proposal) => {
        this.fixed = false
        proposal.manual = false
        this.proposal$.next(proposal)
        this.update()
      })
    ).subscribe()
  }

  public selectTerms(loans: KalpLoan[], terms?: any): void {
    // In the case of "Villkorsändring/Omsättning" special rules apply. Terms should be set on question advice only
    if (this.scenario === Scenarios.Conditions) {
      this.setConditionsTerms(loans)
    } else if (terms) {
      this.setTerms(loans, terms)
    }
  }

  /**
   * Make a new proposal. Can be requested from the outside
   */
  private update(): void {
    if (this.scenario === Scenarios.Conditions) {
      this.setLoans()
    } else {
      this.makeProposal()
    }
  }

  /**
   * Assigns terms to loans for the Conditions scenario (Omsättning/Villkorsändring).
   * The loan with the largest amount gets the term with the highest factor (share per matrix).
   * For equal factors, the longest term goes to the largest loan.
   */
  private setConditionsTerms(loans: KalpLoan[]): void {
    if (loans.length === 0) {
      return
    }

    // For each loan position: get its matrix share (factor) and the term it should receive
    const termsByFactor = Array.from({length: loans.length}, (_, i) => {
      const {factor, effectiveTerm} = this.calculateFactorAndTerms(i, loans.length)
      return {factor, term: effectiveTerm}
    })

    // Sort by largest share first. Equal shares → longest term first
    termsByFactor.sort((a, b) =>
      b.factor === a.factor ? b.term.terms - a.term.terms : b.factor - a.factor
    )

    // Largest loan gets the term with the largest matrix share
    loans
      .toSorted((a, b) => b.amount - a.amount)
      .forEach((loan, i) => Object.assign(loan, termsByFactor[i].term))
  }

  private setTerms(loans: KalpLoan[], terms: Terms[]): void {
    loans.forEach((loan, index) => {
      Object.assign(loan, terms[index])
    })

    // If one loan, the loan should be the longest
    if (loans.length === 1) {
      Object.assign(loans[0], terms[2])
    }

    // If two loans, the last loan should be the longest loan.
    if (loans.length === 2) {
      Object.assign(loans[1], terms[2])
    }
  }

  public makeProposal(): void {
    const numberOfLoans = this.getNumberOfLoans()
    const proposedLoans: KalpLoan[] = []

    let terms: Terms[] = []
    for (let i = 0; i < numberOfLoans; i++) {
      const factorAndTerms = this.calculateFactorAndTerms(i, numberOfLoans)
      terms = factorAndTerms.terms
      const proposedLoan: KalpLoan = new KalpLoan(this.loanAmount * factorAndTerms.factor)

      proposedLoan.new = true
      proposedLoans.push(proposedLoan)
    }
    this.selectTerms(proposedLoans, terms)

    /**
     * flatten the proposed loans to nearest 100 000
     * 966 669 becomes 700 000
     */
    proposedLoans.forEach(loan => {
      loan.amount = Math.round(loan.amount / (25 * 1000)) * 25 * 1000
    })
    const sumOfLoans = proposedLoans.reduce((sum, loan: KalpLoan) => sum + loan.amount, 0)
    // Just adjust the last loan to fill it up to the same as loan amount.
    if (sumOfLoans !== this.loanAmount) {
      proposedLoans[0].amount += (this.loanAmount - sumOfLoans)
    }
    this.kalpService.getActiveBuilding().subscribe((building: KalpBuilding) => {
      proposedLoans.forEach((loan: KalpLoan) => {
        loan.property = building.id
      })
      this.sendProposal(proposedLoans)
    })
  }

  /**
   * Calculates the number of months remaining until a loan expires.
   *
   * @param {KalpLoan} loan - The loan for which to determine the remaining months.
   * @returns {number} The remaining months until the loan expires.
   */
  private monthsLeft(loan: KalpLoan): number {
    // Get the current date in milliseconds
    const currentDate = Date.now()

    // Define the approximate number of milliseconds in a month.
    const millisecondsInAMonth = 30.44 * 24 * 60 * 60 * 1000

    // Calculate the total months left until the loan expires by dividing the time difference
    // between the loan's expiration date and the current date by the milliseconds in a month.
    return Math.floor((loan.expires - currentDate) / millisecondsInAMonth)
  }


  private handleOldLoansSpecialCases(terms: Terms[], hasOddTerms: boolean, hasTwoYear: boolean): void {
    this.kalpService.kalpResult$.pipe(first()).subscribe((kalp) => {
      const oldLoans = kalp[1].loans.loans.filter(loan => !loan.new)
      oldLoans.forEach(oldLoan => {
        const monthsLeft = this.monthsLeft(oldLoan)
        terms.forEach((term, i) => {
          if (monthsLeft > 0) {
            this.handleLoanExpiration(monthsLeft, term, terms, i, hasOddTerms, hasTwoYear)
          }
        })
      })
    })
  }

  private handleLoanExpiration(monthsLeft: number, term: Terms, terms: Terms[], i: number, hasOddTerms: boolean, hasTwoYear: boolean): void {
    if (monthsLeft <= 12) {
      this.handleLoanExpirationWithin12Months(term, terms, i, hasTwoYear)
    } else if (monthsLeft <= 24) {
      this.handleLoanExpirationWithin24Months(term, terms, i, hasOddTerms)
    } else if (monthsLeft <= 36) {
      this.handleLoanExpirationWithin36Months(term, terms, i)
    }
  }

  private handleLoanExpirationWithin12Months(term: Terms, terms: Terms[], i: number, hasTwoYear: boolean): void {
    if (term.terms === 12) {
      terms[i] = TermsTexts.getByMonths(24)
    }

    if (term.terms === 24 && this.getNumberOfLoans() === 3 && !hasTwoYear) {
      terms[i] = TermsTexts.getByMonths(36)
    }
  }

  private handleLoanExpirationWithin24Months(term: Terms, terms: Terms[], i: number, hasOddTerms: boolean): void {
    if (term.terms === 24) {
      terms[i] = TermsTexts.getByMonths(36)
    }

    if (term.terms === 36 && !hasOddTerms) {
      terms[i] = TermsTexts.getByMonths(48)
    }
  }

  private handleLoanExpirationWithin36Months(term: Terms, terms: Terms[], i: number): void {
    if (term.terms === 36) {
      terms[i] = TermsTexts.getByMonths(48)
    }
  }

  /**
   * Ther can be 1 - 3 loans only
   * @private
   */
  private getNumberOfLoans(): number {
    let numberOfLoans = 1

    const fractions = this.isBostadsratt
      ? [1000 * 1000, 2500 * 1000]
      : [1500 * 1000, 3500 * 1000]

    fractions.forEach((i: number) => {
      if (this.loanAmount > i) {
        numberOfLoans++
      }
    })
    return numberOfLoans
  }

  private calculateTerms(terms: Terms[], scenario: number, numberOfLoans: number): void {
    if (numberOfLoans === 1) {
      if (scenario === 1) {
        terms[2] = TermsTexts.getByMonths(12)
      } else if (scenario === 2 || scenario === 3 || scenario === 4) {
        terms[2] = TermsTexts.getByMonths(24)
      } else if (scenario === 5 || scenario === 6) {
        terms[2] = TermsTexts.getByMonths(36)
      }
    } else if (numberOfLoans === 2) {
      if (scenario === 1) {
        terms[0] = TermsTexts.getThreeMonth()
        terms[2] = TermsTexts.getByMonths(12)
      } else if (scenario === 2) {
        terms[0] = TermsTexts.getThreeMonth()
        terms[2] = TermsTexts.getByMonths(24)
      } else if (scenario === 3 || scenario === 4) {
        terms[0] = TermsTexts.getThreeMonth()
        terms[2] = TermsTexts.getByMonths(36)
      } else if (scenario === 5) {
        terms[0] = TermsTexts.getByMonths(12)
        terms[2] = TermsTexts.getByMonths(36)
      } else if (scenario === 6) {
        terms[0] = TermsTexts.getByMonths(24)
        terms[2] = TermsTexts.getByMonths(60)
      }
    } else if (numberOfLoans >= 3) {
      if (scenario === 1 || scenario === 2) {
        terms[0] = TermsTexts.getThreeMonth()
        terms[1] = TermsTexts.getByMonths(12)
        terms[2] = TermsTexts.getByMonths(24)
        terms[3] = TermsTexts.getByMonths(24)
      } else if (scenario === 3 || scenario === 4 || scenario === 5) {
        terms[0] = TermsTexts.getThreeMonth()
        terms[1] = TermsTexts.getByMonths(12)
        terms[2] = TermsTexts.getByMonths(36)
        terms[3] = TermsTexts.getByMonths(36)
      } else if (scenario === 6) {
        terms[0] = TermsTexts.getThreeMonth()
        terms[1] = TermsTexts.getByMonths(36)
        terms[2] = TermsTexts.getByMonths(60)
        terms[3] = TermsTexts.getByMonths(60)
      }
    }
  }

  private getScenario(stayFor: number, maxQ3andQ5: number, maxQ4: number): number {
    let scenario = 0
    if (stayFor === 2) {
      scenario = maxQ3andQ5
      if (maxQ4 === 1) {
        scenario = Math.max(2, scenario)
      } else if (maxQ4 === 2) {
        scenario = Math.max(1, scenario)
      } else if (maxQ4 === 3) {
        scenario = Math.max(3, scenario)
      }

      if (this.twoPercent < 0) {
        scenario = Math.max(3, scenario)
      }

    } else if (stayFor === 3) {
      //Below we +3 because we must use the extra dimension of the questions (se case 4-6 in scripts/scenarios.xls)
      scenario = maxQ3andQ5 + 3
      if (maxQ4 === 1) {
        scenario = Math.max(5, scenario)
      } else if (maxQ4 === 2) {
        scenario = Math.max(4, scenario)
      } else if (maxQ4 === 3) {
        scenario = Math.max(6, scenario)
      }

      if (this.twoPercent < 0) {
        scenario = Math.max(6, scenario)
      }

    }
    return scenario
  }

  private calculateFactor(scenario: number, numberOfLoans: number, i: number): number {
    let factor = 1
    if (numberOfLoans === 2) {
      factor = factor / 2

      if (scenario === 1) {
        factor = i === 0 ? 0.75 : 0.25
      } else if (scenario === 2) {
        factor = i === 0 ? 0.50 : 0.50
      } else if (scenario === 3) {
        factor = i === 0 ? 0.25 : 0.75
      } else if (scenario === 4) {
        factor = i === 0 ? 0.50 : 0.50
      } else if (scenario === 5) {
        factor = i === 0 ? 0.50 : 0.50
      } else if (scenario === 6) {
        factor = i === 0 ? 0.50 : 0.50
      }
    } else if (numberOfLoans === 3) {

      if (scenario === 0) {
        factor = factor / 3
      } else if (scenario === 1) {
        factor = i === 0 ? 0.50 : i === 1 ? 0.25 : 0.25
      } else if (scenario <= 3) {
        factor = i === 0 ? 0.25 : i === 1 ? 0.50 : 0.25
      } else if (scenario === 4) {
        factor = i === 0 ? 0.50 : i === 1 ? 0.25 : 0.25
      } else if (scenario === 5) {
        factor = i === 0 ? 0.25 : i === 1 ? 0.50 : 0.25
      } else if (scenario === 6) {
        factor = i === 0 ? 0.25 : i === 1 ? 0.50 : 0.25
      }
    }
    return factor
  }

  private handleUnansweredQuestions(terms: Terms[]): void {
    terms[0] = TermsTexts.getThreeMonth()
    terms[1] = TermsTexts.getThreeMonth()
    terms[2] = TermsTexts.getThreeMonth()
  }

  private calculateFactorAndTerms(i: number, numberOfLoans: number): {
    factor: number
    terms: Terms[]
    effectiveTerm: Terms
  } {

    const stayFor = this.questions.q6

    const defaultTerms: Terms = {
      interestPercent: 0,
      text: 'Rörlig (3 mån)',
      terms: 3
    }
    const terms: Terms[] = [defaultTerms, defaultTerms, defaultTerms]
    terms[0] = TermsTexts.getThreeMonth()
    terms[1] = TermsTexts.getThreeMonth()
    terms[2] = TermsTexts.getThreeMonth()
    terms[3] = TermsTexts.getThreeMonth()
    terms[4] = TermsTexts.getByMonths(60) //Highest possible if we have >4 loans
    terms[5] = TermsTexts.getByMonths(60) //Highest possible if we have >5 loans
    // If we have >6 loans, they get three months

    const maxQ3andQ5 = Math.max(this.questions.q3, this.questions.q5)
    const maxQ4 = this.questions.q4

    const scenario = this.getScenario(stayFor, maxQ3andQ5, maxQ4)
    const hasOddTerms = scenario === 5 || scenario === 6
    const hasTwoYear = scenario === 4

    this.calculateTerms(terms, scenario, numberOfLoans)

    const factor = this.calculateFactor(scenario, numberOfLoans, i)

    /**
     * Perform very complex operations handling older loans. I've alerted the bank about potential issues arising
     * during question changes, despite our comprehensive set of test cases addressing this.
     * And also that future changes will be more difficult to make. Se this threads for reference:
     * https://linaf.slack.com/archives/C027NQXDG7Q/p1702295829478759
     * https://linaf.slack.com/archives/C027NQXDG7Q/p1706792569241249
     */
    this.handleOldLoansSpecialCases(terms, hasOddTerms, hasTwoYear)

    if (this.questions.q3 === -1 || this.questions.q4 === -1 || this.questions.q5 === -1) {
      this.handleUnansweredQuestions(terms)
    }

    // For 1 loan or the last of 2 loans, setTerms assigns terms[2] (the longest term).
    // For 3+ loans each position maps directly to its index.
    const effectiveTerm: Terms = numberOfLoans <= 2 && i === numberOfLoans - 1 ? terms[2] : terms[i]

    return {factor, terms, effectiveTerm}
  }

  private sendProposal(loans: KalpLoan[]): void {
    const proposal: Proposal = {
      loans: loans,
      loanAmount: this.loanAmount,
      manual: false
    }
    this.kalpService.setOtherLoans(proposal) // Will trigger a re-kalp
    this.updateLoansWithMortgages(proposal.loans)

    /**
     * Needs to do another kalp calculation after proposal update
     */
    this.kalpService.makeKalp()
  }

  /**
   * Sets the loans based on existing loans that need to be omsatta.
   */
  private setLoans(): void {
    let buildingId: string
    this.kalpService.getActiveBuilding().pipe(
      first(),
      switchMap((newBuilding: KalpBuilding) => {
        buildingId = newBuilding.id
        return this.kalpService.loans$
      }),
      first()
    ).subscribe((kalpLoans: KalpLoan[]) => {
      const loans: KalpLoan[] = kalpLoans
        .filter((loan: KalpLoan) => loan.solve && loan.property === buildingId)
        .map((loan: KalpLoan) => {
          const newLoan = new KalpLoan(loan.amount)
          newLoan.interestPercent = loan.interestPercent
          newLoan.property = buildingId
          newLoan.mortgageAmount = loan.mortgageAmount
          newLoan.new = true
          return newLoan
        })
      this.selectTerms(loans)
      this.sendProposal(loans)
    })
  }

  /**
   * Sets the proper mortgage based on mortgage requirement and
   * @private
   */
  private updateLoansWithMortgages(loans: KalpLoan[]): void {
    let requirement: number

    this.getMortgageRequirement().pipe(switchMap(mortgageRequirement => {
      requirement = mortgageRequirement
      return this.kalpService.getActiveBuilding()
    }))
      .subscribe((building: KalpBuilding) => {
        const mortgage = new Mortgage(requirement, building.mortgageRequirementDebt)
        loans.forEach((loan: KalpLoan, index: number) => {
          if (index === 0) {
            if (this.scenario === Scenarios.Conditions) {
              // Each loan already has its per-loan mortgageAmount set in setLoans().
              // Only top up loan[0] if the total falls below the requirement.
              const totalMortgage = loans.reduce((sum, l) => sum + l.mortgageAmount, 0)
              if (mortgage.mortgageAmount > totalMortgage) {
                loan.setMortgageAmount(loan.mortgageAmount + (mortgage.mortgageAmount - totalMortgage))
              }
            } else {
              // For others: new loan only needs to cover the gap above what existing loans already amortize
              loan.setMortgageAmount(Math.max(0, mortgage.mortgageAmount - building.currentMortgage))
            }
          }
        })
        const proposal: Proposal = {
          loans: loans,
          loanAmount: this.loanAmount,
          manual: false
        }
        this.proposal$.next(proposal)
      })
  }

  /**
   * The mortgage requirement based on building loans and the "debt quota"
   * 0, 1, 2 or 3 %. The debt quota is fetched from kalp service it can
   * give an extra percent. The "Building" calculates its own extra based
   * on the loans. So when we have the building, we set the loans on the building
   * and get extra amortization requirements for free.
   * @private
   */
  private getMortgageRequirement(): Observable<number> {
    return this.kalpService.getDebtQuota().pipe(
      first(),
      map((dbq: DebtQuota) => dbq.amortizationRequirement)
    )
  }
}
